import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, RefreshCw, BookOpen } from "lucide-react";
import { useBlogFeed, FeedItem } from "@/hooks/useBlogFeed";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const ITEMS_PER_PAGE = 12;

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function truncate(text: string, maxLen = 160): string {
  if (!text || text.length <= maxLen) return text || "";
  return text.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
}

const BlogCardSkeleton = () => (
  <Card className="overflow-hidden">
    <Skeleton className="h-48 w-full" />
    <div className="p-5 space-y-3">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <div className="flex gap-4 pt-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
      </div>
    </div>
  </Card>
);

const ArticleCard = ({ item }: { item: FeedItem }) => (
  <Link to={`/blog/${item.slug}`} className="group">
    <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      {item.image && (
        <div className="aspect-video overflow-hidden bg-muted">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement!.style.display = "none";
            }}
          />
        </div>
      )}
      <div className="p-5 space-y-3">
        {item.tags && item.tags.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            {item.tags[0]}
          </Badge>
        )}
        <h2 className="text-lg font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">
          {item.title}
        </h2>
        <p className="text-sm text-muted-foreground line-clamp-3">
          {truncate(item.summary || "")}
        </p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(item.date_published)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {item.readingTime} min read
          </span>
        </div>
      </div>
    </Card>
  </Link>
);

const Blog = () => {
  const { items, loading, error, retry } = useBlogFeed();
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  useSEO({
    title: "Blog | Ommni",
    description: "Read our latest articles on commerce operations, product management, and retail technology.",
    keywords: "blog, commerce, retail technology, product management",
    canonical: "https://ommni.io/blog",
  });

  return (
    <PageLayout>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          <Breadcrumbs />

          {/* Hero */}
          <div className="text-center mb-12 mt-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Blog</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Insights, guides, and updates on commerce operations and retail technology.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={retry} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && items.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Articles are on the way!</h2>
              <p className="text-muted-foreground">Check back soon.</p>
            </div>
          )}

          {/* Articles Grid */}
          {!loading && !error && items.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.slice(0, visibleCount).map((item) => (
                  <ArticleCard key={item.id} item={item} />
                ))}
              </div>

              {visibleCount < items.length && (
                <div className="text-center mt-10">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setVisibleCount((c) => c + ITEMS_PER_PAGE)}
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Blog;
